import React, { useMemo } from 'react'
import { rem } from 'polished'
import styled from 'styled-components'
import { pluralizeToString } from 'lib/string/pluralize'
import { buildRoomPackageOptionsViewOrderLPC } from 'lib/offer/offerPageUtil'
import { OfferPageState } from 'contexts/OfferPage/offerPageStateReducer'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import { getDurationPackageOptions, isLPCAdvancedSortingEnabled, OfferPackageOptionView } from 'selectors/offerPage/offerPageSelectors'
import Group from 'components/utils/Group'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import Heading from 'components/Luxkit/Typography/Heading'
import Caption from 'components/Luxkit/Typography/Caption'
import Pane from 'components/Common/Pane'
import { useAppSelector } from 'hooks/reduxHooks'
import AlwaysOnRateOptionMobile from '../AlwaysOnRateOptionMobile'
import { getDatesOccupancyLabel } from 'components/OfferPage/FlashHotel/FlashHotelMobileVariant/FlashHotelMobileVariantUtils'
import usePropertyOfferMapping from 'hooks/Offers/usePropertyOfferMapping'
import config from 'constants/config'
import SinglePropertyPackageSelectionModal from './SinglePropertyPackageSelectionModal'

const StyledModalContent = styled(ModalContent)`
  padding: 0;
`

const RoomHeaderContainer = styled(Group)`
  padding: ${rem(20)};
  padding-top: 0;
  background-color: ${props => props.theme.palette.neutral.default.eight};
`

interface Props {
  room: App.PackageRoomType;
  offer: App.Offer;
  airports?: Array<App.Airport>;
  packageOptions: Array<OfferPackageOptionView>;
  onClose?: () => void;
  onBack?: () => void;
  label: string;
}

function AlwaysOnPackageSelectionModal(props: Props) {
  const {
    room,
    offer,
    airports,
    packageOptions,
    label,
    onClose,
    onBack,
  } = props

  const [propertyMapping] = usePropertyOfferMapping(offer.property?.id, {
    disabled: !config.ACCOMM_USE_LPC_PROPERTY_PAGE,
  })

  const LPCAdvancedSortingEnabled = useAppSelector(isLPCAdvancedSortingEnabled)
  const packageOptionsByRoom = useMemo(() => {
    return buildRoomPackageOptionsViewOrderLPC(packageOptions, LPCAdvancedSortingEnabled)
  }, [packageOptions, LPCAdvancedSortingEnabled])

  const packageViews = useMemo(() => packageOptionsByRoom.find(option => option.roomId === room?.id)?.packageViews ?? [], [packageOptionsByRoom, room?.id])

  const destinationAirport = useMemo(() => airports?.find(({ code }) => code === offer.flightDestinationPort), [airports, offer.flightDestinationPort])

  if (propertyMapping) {
    return <SinglePropertyPackageSelectionModal onBack={onBack} onClose={onClose} />
  }

  return (
    <>
      <ModalHeader
        title="Choose your package"
        subtitle={label}
        onCloseButtonClick={onClose}
        onBackButtonClick={onBack}
      />
      <ModalBody>
        <StyledModalContent>
          <Pane type="light">
            <Group direction="vertical" gap={16}>
              <RoomHeaderContainer direction="vertical" gap={4}>
                <Heading variant="heading5">{room.name}</Heading>
                {!!packageViews.length && <Caption variant="large">{pluralizeToString('option', packageViews.length)} available</Caption>}
              </RoomHeaderContainer>
              {packageViews.map((view, index) => <Pane key={view.package.uniqueKey}>
                <AlwaysOnRateOptionMobile
                  key={view.package.uniqueKey}
                  offer={offer}
                  optionNumber={index + 1}
                  packageView={view}
                  flightDestination={destinationAirport}
                />
              </Pane>)}
            </Group>
          </Pane>
        </StyledModalContent>
      </ModalBody>
    </>
  )
}

export default withOfferPageAndReduxState((state: OfferPageState, appState: App.State) => ({
  room: state.room,
  offer: state.offer,
  rooms: state.rooms,
  airports: appState.geo.airports,
  label: getDatesOccupancyLabel(state),
  packageOptions: getDurationPackageOptions(state, appState),
}))(AlwaysOnPackageSelectionModal)
